import type { FilaDeCartera } from '@/api/cartera.api';
import type { Columna } from '@/components/datos/DataTable';
import type { EdicionDeClientes } from '@/hooks/useEdicionDeClientes';
import { haceCuanto } from '@/lib/athena';
import { fechaLegible, legible } from '@/lib/cartera';
import { formatPhoneNumber } from '@/lib/format';
import { editorDe, type EditableLeadField } from '@/lib/lead-fields';
import { posicionDe } from '@/lib/proceso';
import { claveDeAsignado, columnasEnOrden, gruposDeAsignado, gruposDeEtapa, ordenDeColumnas } from '@/lib/tablero-clientes';
import { CeldaDeAsignado, CeldaDeCliente, CeldaDeEtapa, CeldaDeFicha, CeldaDeProxima, CeldaDeTelefono, CeldaDeTemperatura } from './CeldasDeCliente';

/**
 * LAS COLUMNAS DE «CLIENTES» (tanda 6): las propias (cliente, etapa, temperatura…) y una por cada
 * campo de la ficha del negocio, con el editor de su tipo. Viven aparte de la tabla porque también
 * las usa la barra de herramientas de la pantalla: el panel «Columnas» lista sus títulos y
 * «Exportar» saca sus valores, se esté viendo la tabla o no.
 */
export interface DatosDeColumnas {
  /** Los campos de la ficha del negocio (`useCrmView`): cada uno es una columna. */
  campos: EditableLeadField[];
  etapas: readonly string[];
  /** Las temperaturas con el nombre que les da el negocio (`leadStatusOptionsFrom`). */
  temperaturas: Array<{ value: string; label: string }>;
  ahora: Date;
  edicion: EdicionDeClientes;
  onVerFicha: (fila: FilaDeCartera) => void;
  /** Las que el usuario dejó a la vista, en su orden (`pref.columnas`; `[]` = las de entrada). */
  guardadas: readonly string[];
}

const ORIGEN: Record<FilaDeCartera['origen'], string> = { chat: 'Chat', importado: 'Importado', anuncio: 'Anuncio' };

/** Todas las columnas de este negocio: delante las que se ven, en el orden que eligió el usuario; detrás, las ocultas. */
export function columnasDeClientes({ campos, etapas, temperaturas, ahora, edicion, onVerFicha, guardadas }: DatosDeColumnas): Columna<FilaDeCartera>[] {
  const { asignables } = edicion;
  const etapasConColor = gruposDeEtapa(etapas);
  const asignados = gruposDeAsignado(asignables);

  const propias: Record<string, Columna<FilaDeCartera>> = {
    cliente: {
      id: 'cliente',
      titulo: 'Cliente',
      fija: true,
      valor: (f) => f.nombre,
      celda: (f) => <CeldaDeCliente fila={f} onVerFicha={() => onVerFicha(f)} />,
    },
    etapa: {
      id: 'etapa',
      titulo: 'Etapa',
      ancho: 'min-w-[9.75rem]',
      valor: (f) => etapasConColor[posicionDe(f, etapas)]!.etiqueta,
      celda: (f) => <CeldaDeEtapa fila={f} etapas={etapas} onCambiar={(etapa) => edicion.cambiarEtapa(f, etapa)} />,
    },
    temperatura: {
      id: 'temperatura',
      titulo: 'Temperatura',
      ancho: 'min-w-[9.25rem]',
      valor: (f) => temperaturas.find((t) => t.value === f.conversacion?.temperatura)?.label ?? null,
      celda: (f) => <CeldaDeTemperatura fila={f} opciones={temperaturas} onCambiar={(t) => edicion.cambiarTemperatura(f, t)} />,
    },
    proxima: {
      id: 'proxima',
      titulo: 'Próxima acción',
      // La fecha delante: ordena por cuándo toca, y en el Excel se lee «2026-10-08 Mandarle el brochure».
      valor: (f) => (f.proximaAccion ? [f.proximaAccion.fecha?.slice(0, 10), f.proximaAccion.descripcion].filter(Boolean).join(' ') : null),
      celda: (f) => <CeldaDeProxima fila={f} ahora={ahora} />,
    },
    asignado: {
      id: 'asignado',
      titulo: 'Asignado',
      ancho: 'min-w-[9.5rem]',
      valor: (f) => (f.conversacion ? (asignados.find((g) => g.clave === claveDeAsignado(f, asignables))?.etiqueta ?? null) : null),
      celda: (f) => <CeldaDeAsignado fila={f} asignables={asignables} ocupado={edicion.ocupado} onAsignar={(valor) => edicion.asignar(f, valor)} />,
    },
    ultimo: {
      id: 'ultimo',
      titulo: 'Último mensaje',
      valor: (f) => f.conversacion?.ultimoMensajeEl ?? null,
      celda: (f) => (
        <span className="whitespace-nowrap text-slate-600 dark:text-slate-300">
          {f.conversacion?.ultimoMensajeEl ? haceCuanto(f.conversacion.ultimoMensajeEl, ahora) : <span className="text-slate-500">—</span>}
        </span>
      ),
    },
    telefono: {
      id: 'telefono',
      titulo: 'Teléfono',
      valor: (f) => formatPhoneNumber(f.telefono) || null,
      celda: (f) => <CeldaDeTelefono fila={f} />,
    },
    entro: {
      id: 'entro',
      titulo: 'Entró',
      valor: (f) => f.entroEl,
      celda: (f) => <span className="whitespace-nowrap text-slate-600 dark:text-slate-300">{fechaLegible(f.entroEl)}</span>,
    },
    // T14: de qué base salió (el «Origen» dice el canal: chat, importado, anuncio).
    fuente: {
      id: 'fuente',
      titulo: 'Fuente',
      ancho: 'min-w-[9rem]',
      valor: (f) => f.fuente,
      celda: (f) => (
        <span className="block max-w-[14rem] truncate text-slate-600 dark:text-slate-300" title={f.fuente}>
          {f.fuente}
        </span>
      ),
    },
    origen: {
      id: 'origen',
      titulo: 'Origen',
      valor: (f) => ORIGEN[f.origen] ?? legible(f.origen),
      celda: (f) => <span className="text-slate-600 dark:text-slate-300">{ORIGEN[f.origen] ?? legible(f.origen)}</span>,
    },
  };

  const deFicha = (campo: EditableLeadField): Columna<FilaDeCartera> => {
    const numerica = campo.tipo === 'numero' && !campo.opciones?.length;
    return {
      id: campo.key,
      titulo: campo.label,
      numerica,
      ancho: numerica ? 'min-w-[8.5rem]' : 'min-w-[8rem]',
      valor: (f) => {
        const v = f.ficha[campo.key];
        if (!v) return null;
        return editorDe(campo, v) === 'numero' ? Number(v) : v;
      },
      // Sin el ejemplo de la ficha («Brickell, Edgewater…»): repetido en cada fila vacía, la tabla no se lee.
      celda: (f) => <CeldaDeFicha campo={{ key: campo.key, label: campo.label, tipo: campo.tipo, opciones: campo.opciones }} valor={f.ficha[campo.key] ?? ''} onGuardar={(valor) => edicion.guardarCampo(f, campo.key, valor)} />,
    };
  };

  const todas = ordenDeColumnas(campos.map((c) => c.key));
  return columnasEnOrden(todas, guardadas).flatMap((id) => {
    const campo = campos.find((c) => c.key === id);
    const columna = propias[id] ?? (campo && deFicha(campo));
    return columna ? [columna] : [];
  });
}
